import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Link, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleMinus, faFilter, faUserPlus, faFilterCircleXmark, faIdCard  } from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark} from '@fortawesome/free-regular-svg-icons';
import '../../../styles/variables.css';
import './Transfers.css';
import DsasButton from '../../components/DsasButton/DsasButton.js';
import TeamPicker from './TeamPicker.jsx';
import ArrivalEditor from './ArrivalEditor.jsx';
import TransferPanel from './TransferPanel.jsx';
import { activeMemberships, buildPlayerPayload, idOf, operationId, playerName, previousDay, sortPlayers, today, validateTransfer } from './transferModel.mjs';
import { buildRosterSearchFilters, getRosterDateDefaults, getRosterDateErrors, isPlayerInRosterPeriod } from './transferFilters.mjs';

const normalizeSearch = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr');

// api doit être une référence stable (const de module ou useMemo).
export default function TransfersView({ api, initialProvider, initialDiscipline = '', initialSeason = '', getSeasonDates, disciplines = [], seasons = [], renderProvider, filterInputClassName = '', canCreate = false, canSave = false, onFiltersApplied, onDirtyChange }) {
  const [filters, setFilters] = useState(() => ({ provider: idOf(initialProvider), discipline: idOf(initialDiscipline), season: idOf(initialSeason), competition: '', team: null,
    ...getRosterDateDefaults(initialSeason, getSeasonDates) }));
  const [context, setContext] = useState(null);
  const [players, setPlayers] = useState([]);
  const [rosterFilterOpen, setRosterFilterOpen] = useState(false);
  const [rosterQuery, setRosterQuery] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [arrivalOpen, setArrivalOpen] = useState(false);
  const [arrivalDirty, setArrivalDirty] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [history, setHistory] = useState([]);
  const lock = useRef(false);
  const rosterInput = useRef(null);
  const searchTerms = normalizeSearch(rosterQuery).trim().split(/\s+/).filter(Boolean);
  const visiblePlayerIds = new Set(players.filter(p => searchTerms.every(term => normalizeSearch(playerName(p)).includes(term))).map(idOf));
  const hiddenDepartures = drafts.filter(d => d.kind === 'departure' && !visiblePlayerIds.has(idOf(d.player))).length;
  const dirty = drafts.length > 0 || arrivalDirty;
  const uncertain = drafts.some(d => d.status === 'unknown');
  const disabled = busy || uncertain;
  const dateErrors = getRosterDateErrors(filters);
  const invalidPeriod = !!(dateErrors.startDate || dateErrors.endDate);
  useEffect(() => { if (rosterFilterOpen) rosterInput.current?.focus(); }, [rosterFilterOpen]);
  useEffect(() => { onDirtyChange?.(dirty || busy); return () => onDirtyChange?.(false); }, [dirty, busy, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const resetDrafts = () => { setDrafts([]); setArrivalOpen(false); setArrivalDirty(false); };
  const resetRosterFilter = () => { setRosterQuery(''); setRosterFilterOpen(false); };
  const guard = action => { if (dirty) setPendingChange(() => action); else action(); };
  const changeFilters = next => guard(() => { resetDrafts(); resetRosterFilter(); setContext(null); setPlayers([]); setFilters(next); setError(''); setNotice(''); });
  const remove = key => setDrafts(prev => prev.filter(d => d.key !== key));
  const patch = (key, change) => setDrafts(prev => prev.map(d => d.key === key ? { ...d, ...change } : d));
  const search = async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(''); setNotice('');
    try {
      const r = await api.fetchPlayers(buildRosterSearchFilters(filters));
      const roster = (r.data || []).filter(p => idOf(p.provider) === filters.provider && idOf(p.discipline) === filters.discipline);
      resetDrafts(); resetRosterFilter(); setPlayers(sortPlayers(roster)); setContext({ ...filters });
      onFiltersApplied?.(filters);
    } catch { setError('Impossible de charger les joueurs. Vos saisies sont conservées.'); }
    finally { setBusy(false); lock.current = false; }
  };
  const departure = async p => {
    if (lock.current) return;
    const current = drafts.find(d => idOf(d.player) === idOf(p));
    if (current) { remove(current.key); return; }
    lock.current = true; setBusy(true); setError('');
    try {
      const player = await api.fetchPlayerDetails(p);
      setDrafts(prev => [...prev, { key: operationId(), kind: 'departure', player, sourceTeam: context.team,
        destinationTeam: null, destinationUnknown: false, competition: '', startDate: today() }]);
    } catch (e) { setError(e.message || 'Impossible de charger le joueur.'); }
    finally { setBusy(false); lock.current = false; }
  };
  const addArrival = draft => {
    validateTransfer(draft, context);
    if (draft.isNew && drafts.some(d => d.isNew && playerName(d.player).trim().toLocaleLowerCase('fr') === playerName(draft.player).trim().toLocaleLowerCase('fr'))) throw new Error('Une création avec ce nom est déjà en préparation.');
    if (drafts.some(d => !draft.isNew && idOf(d.player) === idOf(draft.player))) throw new Error('Ce joueur est déjà dans le récapitulatif.');
    setDrafts(prev => [...prev, draft]); setArrivalOpen(false); setArrivalDirty(false);
  };
  const openConfirm = () => {
    setError('');
    try { drafts.forEach(d => validateTransfer(d, context)); setConfirm(true); }
    catch (e) { setError(e.message); }
  };
  const acceptSaved = (draft, result) => {
    setHistory(prev => [...prev, { ...draft, status: 'saved', savedAt: new Date().toISOString() }]);
    remove(draft.key);
    setPlayers(prev => {
      const without = prev.filter(p => idOf(p) !== idOf(result.player));
      return sortPlayers(isPlayerInRosterPeriod(result.player, context) ? [...without, result.player] : without);
    });
  };
  const reconcile = async draft => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const result = await api.resolveTransfer(draft.key);
      if (result?.status !== 'saved') throw new Error('Le transfert n’est pas confirmé.');
      acceptSaved(draft, result); setNotice('Le transfert a été retrouvé et confirmé.');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); lock.current = false; }
  };
  const save = async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    let succeeded = 0;
    try {
      for (const draft of drafts) {
        let payload;
        try { payload = buildPlayerPayload(draft, context); }
        catch (e) { e.outcome = 'not_saved'; throw e; }
        // Retour obligatoire : { status: 'saved', player: ficheCanonique }.
        // En cas d'échec certain : throw avec outcome='not_saved'.
        const result = await api.saveTransfer({ operationId: draft.key, kind: draft.kind, isNew: !!draft.isNew,
          playerId: draft.isNew ? null : idOf(draft.player), player: payload,
          sourceTeamId: idOf(draft.sourceTeam) || null, destinationTeamId: idOf(draft.destinationTeam) || null,
          destinationUnknown: !!draft.destinationUnknown, startDate: draft.startDate, expectedPlayer: draft.player });
        if (result?.status !== 'saved' || !idOf(result.player)) throw new Error('Le serveur n’a pas confirmé le résultat.');
        succeeded++;
        acceptSaved(draft, result);
      }
      setNotice(`${succeeded} transfert(s) enregistré(s).`);
    } catch (e) {
      const failed = drafts[succeeded];
      if (failed && e.outcome !== 'not_saved') patch(failed.key, { status: 'unknown' });
      setError(`${succeeded} transfert(s) confirmé(s). ${e.message || 'Enregistrement interrompu.'}${e.outcome !== 'not_saved' ? ' Résultat incertain : vérifiez côté serveur avant de poursuivre. Les saisies sont conservées.' : ' Les opérations restantes sont conservées et peuvent être réessayées.'}`);
    } finally { setBusy(false); lock.current = false; setConfirm(false); }
  };
  const summary = (items, cancellable) => <Box className="transfers-summary">
    {['departure', 'arrival'].map(kind => {
      const operations = items.filter(d => d.kind === kind);
      if (!operations.length) return null;
      return <Box className="transfers-summary-group" key={kind}>
        <Box className="transfers-editor-header">
          <Typography component="h3" className="transfers-section-title">{kind === 'departure' ? 'Départs' : 'Arrivées'}</Typography>
          <span className="transfers-direction" aria-hidden="true">{kind === 'departure' ? '»' : '«'}</span>
        </Box>
        {operations.map(d => <Box className="transfers-summary-row" key={d.key}>
          <Box className="transfers-summary-details">
            <Typography className="transfers-player-name">{playerName(d.player)}{d.isNew ? ' (création)' : ''}</Typography>
            <Typography className="transfers-summary-route">{d.sourceTeam?.name || 'Sans équipe'} → {d.kind === 'departure' && d.destinationUnknown ? 'Équipe d’arrivée non renseignée' : d.destinationTeam?.name || 'Équipe à préciser'}</Typography>
            <Typography className="transfers-summary-date">Arrivée : {d.startDate || 'à préciser'}{d.status === 'unknown' ? ' — résultat à vérifier' : ''}</Typography>
          </Box>
          {cancellable && <IconButton className="transfers-icon-button" size="small" disabled={disabled} aria-label={`Annuler ${playerName(d.player)}`} onClick={() => remove(d.key)}>
            <FontAwesomeIcon icon={faCircleXmark} />
          </IconButton>}
        </Box>)}
      </Box>;
    })}
  </Box>;


    // console.log(players && players[0] ? activeMemberships(players[0], today()) : null);

  return <Box>
    {/* <Typography component="h1" className="transfers-title">Gestion des transferts</Typography> */}

          {/*PAGE TITLE*/}
          <Box className='centered-medium-wrapper'>
            <h1 className='page-title'>Gestion des transferts</h1>
          </Box>
          <Box className='medium-separator'></Box>
    <Box className="transfers-page">
    <Box className="centered-medium-wrapper">
      <DsasButton label="Rechercher par équipe" variant="primarysub" focused />
    </Box>
    {drafts.filter(d => d.status === 'unknown').map(d => <Button className="transfers-button transfers-button--outline transfers-notice" variant="outlined" key={d.key} disabled={busy} onClick={() => reconcile(d)}>Vérifier l’enregistrement de {playerName(d.player)}</Button>)}
    <Box component="fieldset" disabled={disabled} className="transfers-fieldset transfers-filters">
      {!filters.provider && <Alert severity="warning" className="transfers-notice">Le groupe utilisateur est requis avant de charger les filtres.</Alert>}
      <Box className={`transfers-filter-fields${renderProvider ? ' transfers-filter-fields--provider' : ''}`}>
        {renderProvider ? renderProvider({ value: filters.provider, disabled, onChange: value => changeFilters({ ...filters, provider: idOf(value), competition: '', team: null }) }) : null}
        {[['discipline', 'Discipline', disciplines], ['season', 'Saison', seasons]].map(([key, label, options]) => <TextField key={key} select fullWidth size="medium" className={filterInputClassName} label={label} value={filters[key]} disabled={disabled}
          SelectProps={{ MenuProps: { className: 'transfers-filter-menu' } }}
          onChange={e => changeFilters({ ...filters, [key]: e.target.value,
            ...(key === 'season' ? getRosterDateDefaults(e.target.value, getSeasonDates) : {}), competition: '', team: null })}>
          {options.map(o => <MenuItem key={idOf(o)} value={idOf(o)}>{o.name}</MenuItem>)}
        </TextField>)}
        <TeamPicker key={`${filters.provider}-${filters.discipline}-${filters.season}`} api={api} context={filters} value={filters} disabled={disabled}
          size="medium" inputClassName={filterInputClassName} menuClassName="transfers-filter-menu"
          onChange={v => changeFilters({ ...filters, ...v })} />
        <Box className="transfers-fields-row transfers-filter-period">
          <TextField fullWidth size="medium" className={filterInputClassName} type="date" label="Date de début" name="startDate"
            InputLabelProps={{ shrink: true }} inputProps={{ max: filters.endDate || undefined }}
            value={filters.startDate} disabled={disabled} error={!!dateErrors.startDate} helperText={dateErrors.startDate || undefined}
            onChange={e => changeFilters({ ...filters, startDate: e.target.value })} />
          <TextField fullWidth size="medium" className={filterInputClassName} type="date" label="Date de fin" name="endDate"
            InputLabelProps={{ shrink: true }} inputProps={{ min: filters.startDate || undefined }}
            value={filters.endDate} disabled={disabled} error={!!dateErrors.endDate} helperText={dateErrors.endDate || undefined}
            onChange={e => changeFilters({ ...filters, endDate: e.target.value })} />
        </Box>
      </Box>
      <Box className="transfers-search-actions">
        <DsasButton label="Afficher les joueurs" variant="secondary" disabled={disabled || invalidPeriod || !filters.provider || !filters.discipline || !filters.season || !filters.team} onClick={() => guard(search)} />
      </Box>
    </Box>
    {context && <>
      <Typography component="h2" className="transfers-team-title"></Typography>
      <Box className="transfers-layout">
        <Box className="transfers-main">
          <Box className="transfers-roster-tools">
            <Box className="transfers-roster-toolbar">
              <Button className="transfers-button transfers-button--outline" variant="outlined"
                startIcon={rosterFilterOpen ? <FontAwesomeIcon icon={faFilterCircleXmark} /> : <FontAwesomeIcon icon={faFilter} />} aria-expanded={rosterFilterOpen}
                aria-controls="transfers-roster-filter" onClick={() => {
                  if (rosterFilterOpen) resetRosterFilter(); else setRosterFilterOpen(true);
                }}>{rosterFilterOpen ? 'Supprimer le filtre' : 'Filtrer l’effectif'}</Button>
              <Typography className="transfers-roster-count" role="status">
                {visiblePlayerIds.size} joueur(s) affiché(s) sur {players.length}
              </Typography>
            </Box>
            <Box id="transfers-roster-filter" className="transfers-roster-filter" hidden={!rosterFilterOpen}>
              <TextField fullWidth size="medium" className={filterInputClassName} type="search"
                label="Nom ou prénom dans l’effectif" placeholder="Ex. : Jean Dupont" inputRef={rosterInput}
                value={rosterQuery} onChange={e => setRosterQuery(e.target.value)}
                helperText="Filtre uniquement les joueurs déjà chargés. Les transferts en cours sont conservés." />
              {/* <Button className="transfers-button transfers-button--outline transfers-align-start" disabled={!rosterQuery}
                variant="outlined" startIcon={<FontAwesomeIcon icon={faFilterCircleXmark} />}
                onClick={() => { setRosterQuery(''); rosterInput.current?.focus(); }}></Button> */}
            </Box>
            {!!hiddenDepartures && <Typography className="transfers-roster-count">
              {hiddenDepartures} départ(s) en préparation masqué(s) par le filtre, toujours présent(s) dans le récapitulatif.
            </Typography>}
          </Box>
          <TableContainer component={Paper} variant="outlined" className="transfers-table-container">
            <Table size="small" className="transfers-table" aria-label="Joueurs de l’équipe">
              <TableHead><TableRow>{['','Nom', 'Prénom', 'Club(s) actuel(s)', 'Action'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {!players.length && <TableRow><TableCell colSpan={5} className="transfers-empty">Aucun joueur dans cette équipe.</TableCell></TableRow>}
                {!!players.length && !visiblePlayerIds.size && <TableRow><TableCell colSpan={5} className="transfers-empty">Aucun joueur ne correspond au filtre.</TableCell></TableRow>}
                {players.map(p => {
                  const draft = drafts.find(d => d.kind === 'departure' && idOf(d.player) === idOf(p));
                  const hidden = !visiblePlayerIds.has(idOf(p));
                  return <Fragment key={idOf(p)}>
                    <TableRow hidden={hidden} className={draft ? 'transfers-player-row transfers-player-row--selected' : 'transfers-player-row'}>
                      <TableCell>
                         <Link
                            component="a"
                            href={`/sports/players/create/${idOf(p)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Ouvrir la fiche de ${playerName(p)} (nouvel onglet)`}
                            underline="hover"
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.4,
                              fontWeight: 'inherit',
                              color: 'inherit',
                              cursor: 'pointer',
                            }}
                            title="Ouvrir la fiche dans un nouvel onglet"
                          >
                          <FontAwesomeIcon icon={faIdCard} size="xl" />
                        </Link>
                      </TableCell>
                      <TableCell>{p.lastname}</TableCell>
                      <TableCell>{p.firstname}</TableCell>
                      <TableCell>{activeMemberships(p).map(t => t.name || t.team?.name || idOf(t.team)).join(', ') || 'Aucune affiliation active'}</TableCell>
                      <TableCell className="transfers-action-cell"><Button className="transfers-button transfers-button--outline transfers-departure-button" aria-pressed={!!draft} variant="outlined" startIcon={<FontAwesomeIcon icon={faCircleMinus} />} disabled={disabled || !canSave} onClick={() => departure(p)}>Départ</Button></TableCell>
                    </TableRow>
                    {draft && <TableRow hidden={hidden}><TableCell colSpan={5} className="transfers-departure-cell">
                      <TransferPanel className="transfers-editor transfers-departure-editor"
                        title={`Départ de ${playerName(p)}`} toggleLabel={`le départ de ${playerName(p)}`}>
                        <Typography className="transfers-help">Indiquer l’équipe dans laquelle le joueur est parti.</Typography>
                        <FormControlLabel className="transfers-destination-unknown"
                          control={<Checkbox size="small" checked={!!draft.destinationUnknown} disabled={disabled}
                            onChange={e => {
                              const checked = e.target.checked;
                              patch(draft.key, checked
                                ? { destinationUnknown: true, competition: '', destinationTeam: null }
                                : { destinationUnknown: false });
                            }} />}
                          label="Équipe d’arrivée non renseignée" />
                        <TeamPicker api={api} context={context} value={{ competition: draft.competition, team: draft.destinationTeam }} exclude={context.team} disabled={disabled || !!draft.destinationUnknown}
                          onChange={v => patch(draft.key, { competition: v.competition, destinationTeam: v.team })} />
                        <Box className="transfers-fields-row">
                          <TextField fullWidth size="small" label="Date d’arrivée" type="date" InputLabelProps={{ shrink: true }} value={draft.startDate} disabled={disabled} onChange={e => patch(draft.key, { startDate: e.target.value })} />
                          <TextField fullWidth size="small" className="transfers-readonly-field" label="Fin dans l’équipe actuelle (J−1)" InputLabelProps={{ shrink: true }} value={draft.startDate ? previousDay(draft.startDate) : ''} InputProps={{ readOnly: true }} />
                        </Box>
                        <Button className="transfers-button transfers-button--text transfers-align-start" startIcon={<FontAwesomeIcon icon={faCircleXmark} />} disabled={disabled} onClick={() => remove(draft.key)}>Annuler le départ</Button>
                      </TransferPanel>
                    </TableCell></TableRow>}
                  </Fragment>;
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box component="section" className="transfers-arrivals" aria-labelledby="transfers-arrivals-title">
            <Typography component="h2" id="transfers-arrivals-title" className="transfers-section-title">Arrivées</Typography>
            {drafts.filter(d => d.kind === 'arrival').map(d => <TransferPanel key={d.key}
              className="transfers-editor transfers-arrival-draft" title={playerName(d.player)}
              toggleLabel={`l’arrivée de ${playerName(d.player)}`} defaultExpanded={false}
              actions={<Button className="transfers-button transfers-button--text" startIcon={<FontAwesomeIcon icon={faCircleXmark} />}
                aria-label={`Annuler l’arrivée de ${playerName(d.player)}`} disabled={disabled} onClick={() => remove(d.key)}>Annuler</Button>}>
              <Typography className="transfers-summary-route">{d.sourceTeam?.name || 'Sans équipe'} → {d.destinationTeam?.name}</Typography>
              <Typography className="transfers-summary-date">Arrivée : {d.startDate}{d.sourceTeam ? ` · Fin dans l’équipe de départ : ${previousDay(d.startDate)}` : ''}</Typography>
              {d.isNew && <Typography className="transfers-player-name">Nouveau joueur</Typography>}
              <Box component="dl" className="transfers-player-details">
                {[['birthday', 'Date de naissance'], ['nationality', 'Nationalité'], ['positioning', 'Poste'], ['number', 'Numéro']]
                  .filter(([key]) => d.player[key] !== undefined && d.player[key] !== null && d.player[key] !== '')
                  .map(([key, label]) => <Box key={key}>
                    <Typography component="dt">{label}</Typography>
                    <Typography component="dd">{key === 'birthday' ? String(d.player[key]).slice(0, 10) : String(d.player[key])}</Typography>
                  </Box>)}
              </Box>
            </TransferPanel>)}
            {arrivalOpen ? <Box component="fieldset" disabled={disabled} className="transfers-fieldset">
              <ArrivalEditor api={api} context={context} canCreate={canCreate} onAdd={addArrival} existingIds={drafts.filter(d => !d.isNew).map(d => idOf(d.player))}
                onDirty={() => setArrivalDirty(true)} onCancel={() => { setArrivalOpen(false); setArrivalDirty(false); }} />
            </Box> : <Button className="transfers-button transfers-button--outline transfers-align-start" variant="outlined" startIcon={<FontAwesomeIcon icon={faUserPlus} />} disabled={disabled || !canSave} onClick={() => setArrivalOpen(true)}>Ajouter un joueur</Button>}
          </Box>
        </Box>
        <Paper component="aside" variant="outlined" className="transfers-recap" aria-labelledby="transfers-recap-title">
          <Typography component="h2" id="transfers-recap-title" className="transfers-recap-title">Récapitulatif</Typography>
          {!drafts.length ? <Typography className="transfers-empty">Aucun transfert en cours.</Typography> : summary(drafts, true)}
        </Paper>
      </Box>
      {error && <Alert severity="error" className="transfers-error">{error}</Alert>}
              <Box className="transfers-submit">
                <Button fullWidth className="transfers-button transfers-button--primary" variant="contained" disableElevation disabled={disabled || !canSave || !api.saveTransfer || !drafts.length || arrivalOpen} onClick={openConfirm}>Valider les transferts ({drafts.length})</Button>
              </Box>
      {notice && <Alert severity="success" className="transfers-success">{notice}</Alert>}

      {!!history.length && <Box component="section" className="transfers-history"><Typography component="h2" className="transfers-section-title">Transferts enregistrés pendant cette session</Typography>{summary(history, false)}</Box>}
    </>}
    <Dialog className="transfers-dialog" open={!!pendingChange} onClose={() => setPendingChange(null)} aria-labelledby="discard-title" aria-describedby="discard-description">
      <DialogTitle id="discard-title">Abandonner les saisies ?</DialogTitle>
      <DialogContent id="discard-description">Attention, cette nouvelle recherche vous fera perdre les transferts et les saisies non enregistrés. Voulez-vous continuer ?</DialogContent>
      <DialogActions>
        <Button className="transfers-button transfers-button--outline" variant="outlined" onClick={() => setPendingChange(null)}>Conserver ma saisie</Button>
        <Button className="transfers-button transfers-button--primary" variant="contained" disableElevation onClick={() => { const action = pendingChange; setPendingChange(null); action(); }}>Continuer</Button>
      </DialogActions>
    </Dialog>
    <Dialog className="transfers-dialog" open={confirm} onClose={() => { if (!busy) setConfirm(false); }} fullWidth maxWidth="sm" aria-labelledby="confirm-title">
      <DialogTitle id="confirm-title" className="transfers-dialog-title-with-close">
        Confirmer les transferts
        <IconButton className="transfers-icon-button transfers-dialog-close" size="small" disabled={busy} aria-label="Fermer la confirmation" onClick={() => setConfirm(false)}><FontAwesomeIcon icon={faCircleXmark} /></IconButton>
      </DialogTitle>
      <DialogContent>{summary(drafts, false)}</DialogContent>
      <DialogActions>
        <Button className="transfers-button transfers-button--outline" variant="outlined" disabled={busy} onClick={() => setConfirm(false)}>Annuler</Button>
        <Button className="transfers-button transfers-button--primary" variant="contained" disableElevation disabled={busy} onClick={save}>{busy ? 'Enregistrement…' : 'Valider'}</Button>
      </DialogActions>
    </Dialog>
    </Box>
  </Box>;
}
